import { InboxOutlined } from "@ant-design/icons";
import { Button, Col, Dropdown, Menu, Progress, Row, Typography, Upload } from "antd";
import { UploadFile } from "antd/es";
import { useState } from "react";
import Icon from "../../assets/icons/icon";
import EnumSelect from "../../components/form/enumSelect";
import LoadingContainer from "../../components/loadingContainer";
import { renderErrorMessage } from "../../components/messages/errorMessage";
import {
  useTaxLegalActions,
  useTaxLegalFiles,
  useTaxLegalId
} from "../../contexts/taxLegalCaseStore";
import useIsDraggingFiles from "../../hooks/useIsDraggingFiles";
import _t from "../../lang/translate";
import { deleteFile, openFileLink, saveFile, updateFile } from "../../services/mediaService";
import {
  downloadAllFiles,
  taxLegalFileUrl
} from "../../services/taxLegalService";
import { ApiFile } from "../../types/appTypes";
import { getFileId, isApiFile, isUploadFile } from "../../utilities/convertTypes";
import format from "../../utilities/formatNumbers";
import { capitalize } from "../../utilities/stringExtraFunctions";

type Props = {
  isModal?: boolean
}
export default function TaxLegalCaseAttachments({ isModal }: Props) {
  const taxLegalCaseId = useTaxLegalId()
  const { setFiles } = useTaxLegalActions()
  const files = useTaxLegalFiles() as UploadFile<ApiFile>[];
  const [isDragging] = useIsDraggingFiles({ isModal: !!isModal });
  const [loadingIds, setLoadingIds] = useState<(string | number)[]>([])



  const handleFileUpload = async (options: any) => {
    const { file, action, onProgress, onSuccess, onError } = options;
    const data = new FormData();
    data.append("file", file);
    try {
      const res = await saveFile(action, data, {
        onUploadProgress: (e) => {
          onProgress({ percent: (e.loaded / e.total) * 100 }, file);
        },
      });
      onSuccess(res.data, file);
    } catch (ex) {
      onError(ex);
    }
  };

  async function setFileCategory(file: ApiFile | UploadFile<ApiFile>, categoryId: number) {
    const id = getFileId(file)
    try {
      setLoadingIds(prev => [id, ...prev])
      const { data: newFile } = await updateFile(taxLegalFileUrl(taxLegalCaseId), id, { category: categoryId, isAdmin: true })
      const newFiles = files?.map(f => {
        if (getFileId(f) !== id) return f;
        return { ...f, ...newFile }
      })
      setFiles(newFiles ?? [])
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoadingIds(prev => prev.filter(item => item !== id))
    }
  }

  const handleDownloadAll = async () => {
    try {
      setLoadingIds(files?.map(f => getFileId(f)) ?? [])
      const { data } = await downloadAllFiles(taxLegalCaseId);
      if (data?.url) window.open(data.url, "_blank");
    } catch (error) {
      renderErrorMessage(error);
    } finally {
      setLoadingIds([])
    }
  };


  async function handleDownloadFile(file: UploadFile | ApiFile) {
    const id = getFileId(file);
    try {
      setLoadingIds(prev => [id, ...prev])
      await openFileLink(file.url || file.response?.url)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoadingIds(prev => prev.filter(item => item !== id))
    }
  }

  async function handleDeleteFile(file: UploadFile | ApiFile) {
    const id = getFileId(file);
    try {
      setLoadingIds(prev => [id, ...prev])
      await deleteFile(taxLegalFileUrl(taxLegalCaseId), id);
      setFiles(files?.filter(f => getFileId(f) !== id) ?? [])
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoadingIds(prev => prev.filter(item => item !== id))
    }
  }

  // Ant design will return UploadFile, however the server returns ApiFile.
  return (

    <div>
      <div>
        <Upload.Dragger
          customRequest={handleFileUpload}
          action={taxLegalFileUrl(taxLegalCaseId)}
          multiple
          fileList={files ?? []}
          className={`${isDragging ? "is-dragging" : ""} tax-legal-file-drop-zone`}
          onSuccess={(apiFile: ApiFile, file) => {
            // Merge the server ApiFile with the antd UploadFile
            const newFile: UploadFile<ApiFile> = {
              ...file,
              ...apiFile,
              name: apiFile.name ?? file.name,
              url: apiFile.url ?? "",
              response: { ...apiFile },
            }
            const newFiles = files?.map(f => {
              // compare the antd id of the file instead of the serverId, because the files list does not yet contain the server ID  that this point.
              if (getFileId(f) !== getFileId(file)) return f
              return newFile
            })
            setFiles(newFiles ?? [])
          }}
          onChange={({ file, fileList }) => {
            setFiles(fileList)
          }}
          itemRender={(originNode, file: UploadFile<ApiFile> | ApiFile, fileList) => {
            return (
              <div className="mt-05 border-light pt-03 pb-03 pl-05 pr-05 border-rounded">
                <LoadingContainer loading={loadingIds.includes(getFileId(file))}>
                  <Row justify="space-between" align="middle" gutter={8} wrap={false}>
                    <Col flex={1} style={{ display: "flex", alignItems: "center" }}>
                      <Typography.Link onClick={() => handleDownloadFile(file)} className="mr-05">
                        <Icon name="attach-outline" size="large" />
                      </Typography.Link>
                      <div style={{ position: "relative", flex: "1 1 auto" }}>
                        <Typography.Link className="text-small text-ellipsis position-absolute w-100"
                          title={file.name ?? ""}
                          onClick={() => handleDownloadFile(file)}
                        > {capitalize(file.name ?? "")} </Typography.Link>

                        {isUploadFile(file) && file.status === "uploading" ? (
                          <div style={{ paddingTop: 20 }}>
                            <Progress type="line" showInfo={false} percent={file.percent} />
                          </div>
                        ) : (
                          <small style={{ display: "inline-block", paddingTop: 20 }}>{file.size && format.fileSize(file.size)} - Uploadet</small>
                        )}
                      </div>
                    </Col>
                    <Col className="d-flex">
                      <EnumSelect style={{ width: 130 }} dropdownMatchSelectWidth={200} nameKey={['mediaCategory']} value={isApiFile(file) && file.category} onChange={categoryId => setFileCategory(file, categoryId)} />
                      <Dropdown overlay={
                        <Menu items={[
                          {
                            icon: <Icon name="trash-outline" />,
                            danger: true,
                            key: "delete",
                            label: _t("delete"),
                            onClick: () => handleDeleteFile(file)
                          }
                        ]} />
                      }>
                        <Button className="ml-03" shape="circle" type="text">
                          <Icon size="medium" name="ellipsis-vertical-sharp" />
                        </Button>
                      </Dropdown>
                    </Col>
                  </Row>
                </LoadingContainer>
              </div>
            )
          }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">{isDragging ? _t("msg.drop_her_to_attach") : _t("upload_your_files_here")}</p>
        </Upload.Dragger>
      </div>

      <div className="text-right mt-05">
        <strong>{files?.length}</strong> {_t("files_in_attached_to_case")}
        {!!files?.length && (
          <>
            ,&nbsp;
            <Button type="link" onClick={handleDownloadAll} className="p-0" size="small">
              {_t("download", "all")}
            </Button>
          </>
        )}
      </div>

    </div>
  );
}
